import React from 'react';
import { Card, Tooltip } from 'antd';
import {
  ClockCircleOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const COLORS = {
  success: '#52C41A',
  warning: '#FAAD14',
  danger: '#FF4D4F',
  brand: '#00A651',
  blue: '#1890ff',
};

/**
 * Module 2: Fulfillment Pipeline
 * Horizontal step-based pipeline showing order flow with counts and avg times
 */
const FulfillmentPipeline = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const nodes = [
    {
      label: t('dashboard.pipeline.waiting'),
      icon: <ClockCircleOutlined />,
      count: data.waitingOrders,
      time: data.avgWaitTime,
      timeLabel: t('dashboard.pipeline.avgWait'),
      color: COLORS.warning,
      bg: '#fffbe6',
      timeWarning: data.avgWaitTime > 10,
    },
    {
      label: t('dashboard.pipeline.pickAndPack'),
      icon: <ShoppingCartOutlined />,
      count: data.pickingOrders,
      time: data.avgPickingTime,
      timeLabel: t('dashboard.pipeline.avgPick'),
      color: COLORS.blue,
      bg: '#e6f7ff',
      subMetrics: [
        { label: t('dashboard.pipeline.avgPickTimeSKU'), value: `${data.skuPickingTime} ${t('dashboard.min')}` },
        { label: t('dashboard.pipeline.avgPickAndPack'), value: `${data.avgPickAndPackTime} ${t('dashboard.min')}` },
      ],
    },
    {
      label: t('dashboard.pipeline.readyToDelivery'),
      icon: <CheckCircleOutlined />,
      count: data.completedOrders,
      time: null,
      timeLabel: null,
      color: COLORS.success,
      bg: '#f6ffed',
      subMetrics: [
        { label: t('dashboard.pipeline.avgWaitingForDelivery'), value: `${data.avgWaitingForDelivery} ${t('dashboard.min')}` },
      ],
    },
  ];

  return (
    <Card
      title={t('dashboard.pipeline.title')}
      extra={
        <div style={{ display: 'flex', gap: 24, fontSize: 13, alignItems: 'center' }}>
          <span>
            {t('dashboard.pipeline.endToEnd')}: <b style={{ color: COLORS.danger, fontSize: 16 }}>{data.totalProcessingTime} {t('dashboard.min')}</b>
            <Tooltip 
              title={
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{t('dashboard.pipeline.endToEndCalculation')}:</div>
                  <div>{t('dashboard.pipeline.avgWait')} ({data.avgWaitTime} {t('dashboard.min')})</div>
                  <div>+ {t('dashboard.pipeline.avgPickAndPack')} ({data.avgPickAndPackTime} {t('dashboard.min')})</div>
                  <div>+ {t('dashboard.pipeline.avgWaitingForDelivery')} ({data.avgWaitingForDelivery} {t('dashboard.min')})</div>
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.3)', marginTop: 4, paddingTop: 4 }}>
                    = {data.totalProcessingTime} {t('dashboard.min')}
                  </div>
                </div>
              }
            >
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', cursor: 'pointer' }} />
            </Tooltip>
          </span>
        </div>
      }
      styles={{ body: { padding: '16px 24px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
        {nodes.map((node, index) => (
          <React.Fragment key={node.label}>
            {/* Pipeline Node */}
            <div
              style={{
                flex: 1,
                background: node.bg,
                borderRadius: 8,
                padding: '16px 12px',
                border: `1px solid ${node.color}22`,
                position: 'relative',
                minWidth: 0,
              }}
            >
              {/* Header with icon */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <span style={{ fontSize: 18, color: node.color }}>{node.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{node.label}</span>
              </div>

              {/* Count - big number on top */}
              <div style={{ fontSize: 36, fontWeight: 'bold', color: node.color, lineHeight: 1, marginBottom: 8 }}>
                {typeof node.count === 'number' ? node.count.toLocaleString() : node.count}
              </div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>{t('dashboard.orders')}</div>

              {/* Average time below */}
              {node.time !== null && (
                <div
                  style={{
                    fontSize: 14,
                    color: node.timeWarning ? COLORS.danger : '#666',
                    fontWeight: node.timeWarning ? 'bold' : 'normal',
                    marginBottom: 4,
                  }}
                >
                  {node.timeLabel}: <b>{node.time} {t('dashboard.min')}</b>
                </div>
              )}

              {/* Sub-metrics if any */}
              {node.subMetrics && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
                  {node.subMetrics.map((m) => (
                    <div key={m.label} style={{ fontSize: 12, color: '#888' }}>
                      {m.label}: <b>{m.value}</b>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Arrow between nodes */}
            {index < nodes.length - 1 && (
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', color: '#d9d9d9' }}>
                <ArrowRightOutlined style={{ fontSize: 20 }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
};

export default FulfillmentPipeline;
